import { useCallback, useEffect, useState } from "react";
import { Button, useNotify } from "react-admin";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { settings } from "app";
import permissions from "app/permissions";
import { erasmusRequests } from "api/erasmusApi";
import { SectionTitle } from "features/SectionTitle";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import SecuredComponent from "server/security/SecuredComponent";
import { useNotifyError } from "utils/useNotifyError";
import DocumentFileLink from "../../Partials/DocumentFileLink";
import UploadDocumentDialog from "./UploadDocumentDialog";

const AttachedDocumentsSection = ({ id }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notify = useNotify();
  const notifyError = useNotifyError();

  const [documents, setDocuments] = useState([]);
  // Set only after a successful load, so the "no documents" message never shows for a failed GET.
  const [loaded, setLoaded] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        erasmusRequests.incomingStudentsRegularDocuments(id),
        null,
        "GET",
      );
      setDocuments(data ?? []);
      setLoaded(true);
    } catch (err) {
      notifyError(err);
    }
  }, [id, notifyError]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = async documentRowId => {
    await dataProvider.customRequest(
      erasmusRequests.deleteIncomingStudentsRegularDocument(id, documentRowId),
      null,
      "DELETE",
    );
    notify("Документът е изтрит успешно", { type: "success" });
    await fetchDocuments();
  };

  return (
    <>
      <SectionTitle
        paddingY={2}
        title="Прикачени документи"
        textAlign="center"
      />
      <Grid
        container
        spacing={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        {documents.length ? (
          documents.map(document => (
            <Grid
              item
              xs={12}
              md={4}
              key={document.id}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <DocumentFileLink
                  id={document.documentId}
                  documentName={document.name}
                />
                <SecuredComponent permission={permissions.canManageErasmusRegularStudents}>
                  <ConfirmModal
                    titleContent="Изтриване на документ"
                    message={`Сигурни ли сте, че искате да изтриете "${document.name}"?`}
                    onConfirm={() => handleDelete(document.id)}
                    saveTitle="Изтрий"
                    triggerButton={
                      <Button
                        label="Изтрий"
                        color="error"
                        startIcon={<DeleteIcon />}
                      />
                    }
                  />
                </SecuredComponent>
              </Box>
            </Grid>
          ))
        ) : loaded ? (
          <Grid
            item
            xs={12}
          >
            <Typography>Няма прикачени документи.</Typography>
          </Grid>
        ) : null}
        <SecuredComponent permission={permissions.canManageErasmusRegularStudents}>
          <Grid
            item
            xs={12}
          >
            <UploadDocumentDialog
              id={id}
              onUploaded={fetchDocuments}
            />
          </Grid>
        </SecuredComponent>
      </Grid>
    </>
  );
};

export default AttachedDocumentsSection;
